import React from 'react'
import { TaskSummary } from '@elements/Questions/questionTypes/Inspection/TaskSummary'
import { Flex, Text } from '@fundamentals'
import { Avatar, Button, Card } from '@common/material'
import {
  responseOptionsObject,
  ResponseValue,
} from '@elements/Questions/questionTypes'
import { localizedStrings } from '@core/strings'
import {
  IInspectionItemAnswer,
  InspectionItemAnswer,
} from '@core/entities/InspectionItemAnswer'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'
import { useRouter } from 'next/router'
import { PageUrls } from '@core/page-urls'
import { useNavigationContext } from '@common/hooks'
import { CardActionArea } from '@mui/material'

const InspectionTaskCardActionWrapper: React.FC<{
  clickable: boolean
  onActionClick: () => void
}> = ({ children, onActionClick, clickable }) => {
  if (clickable) {
    return (
      <CardActionArea sx={{ p: 2 }} onClick={onActionClick}>
        {children}
      </CardActionArea>
    )
  }
  return <>{children}</>
}

type ComponentProps = {
  inspectionItemAnswer: IInspectionItemAnswer
  readOnly: boolean
  onChangeResponse: (
    initialValues: IInspectionItemAnswer,
    openTaskModal?: boolean,
  ) => void
}

export const InspectionAnswer: React.FC<ComponentProps> = ({
  inspectionItemAnswer,
  readOnly,
  onChangeResponse,
  ...props
}) => {
  if (!inspectionItemAnswer) return null
  const { organisationId } = useNavigationContext()
  const router = useRouter()
  const responseType =
    responseOptionsObject[inspectionItemAnswer.inspectionItemAnswerType]

  // before submitting form
  const taskPayload = inspectionItemAnswer?.taskPayload
  // after submission
  const inspectionTask = inspectionItemAnswer?.inspectionTask

  const attachments = [
    ...(inspectionItemAnswer?.submissionProofs?.map((proof) => ({
      ...proof,
      type: 'image',
    })) || []),
    ...(inspectionItemAnswer?.submissionDocuments?.map((document) => ({
      ...document,
      type: 'application/pdf',
    })) || []),
  ]

  const handleCardClick = () => {
    if (inspectionTask) {
      router.push(PageUrls.task(organisationId, inspectionTask.id), undefined, {
        shallow: true,
      })
    }
    if (taskPayload) {
      onChangeResponse(inspectionItemAnswer, true)
    }
    if (
      inspectionItemAnswer.inspectionItemAnswerType === ResponseValue.YES &&
      !readOnly
    ) {
      onChangeResponse(inspectionItemAnswer)
    }
  }

  const clickable =
    !!inspectionTask ||
    (!!taskPayload && !readOnly) ||
    !!(
      inspectionItemAnswer.inspectionItemAnswerType === ResponseValue.YES &&
      !readOnly
    )

  const answerTypeColors = InspectionItemAnswer.getAnswerTypeConfigColors(
    inspectionItemAnswer.inspectionItemAnswerType,
  )

  if (inspectionItemAnswer.inspectionItemAnswerType === ResponseValue.NA) {
    return (
      <Flex alignItems='center' mt={2} {...props}>
        <Avatar
          sx={{
            bgcolor: answerTypeColors.selected.backgroundColor,
            mr: 1,
            width: 30,
            height: 30,
          }}
        >
          <Text fontSize={10}>{responseType?.label}</Text>
        </Avatar>
        {!readOnly && (
          <Button
            onClick={() => onChangeResponse(inspectionItemAnswer)}
            data-test='change-response-btn'
          >
            {localizedStrings.changeResponse}
          </Button>
        )}
      </Flex>
    )
  }

  return (
    <Card
      sx={{
        mb: 2,
        p: !clickable && 2,
      }}
      {...props}
    >
      <InspectionTaskCardActionWrapper
        onActionClick={handleCardClick}
        clickable={clickable}
      >
        <Avatar
          sx={{
            bgcolor: answerTypeColors.selected.backgroundColor,
            width: 30,
            height: 30,
          }}
        >
          {responseType?.label}
        </Avatar>
        {(taskPayload || inspectionTask) && (
          <TaskSummary
            templateName={
              taskPayload?.taskTemplate?.name || inspectionTask?.template.name
            }
            summary={taskPayload?.summary || inspectionTask?.summary}
            priority={taskPayload?.priority || inspectionTask?.priority}
            dueDate={taskPayload?.dueDate || inspectionTask?.dueDate}
            user={taskPayload?.assigneeUser || inspectionTask?.assignee}
            location={
              taskPayload?.projectLocations?.locations?.[0]?.locationName ||
              inspectionTask?.location
            }
          />
        )}
        {inspectionItemAnswer?.submissionText && (
          <Text variant='body1' mt={1} data-test='additional-info-text'>
            {inspectionItemAnswer.submissionText}
          </Text>
        )}
        {!!attachments.length && (
          <AttachmentInput
            heading={localizedStrings.proofs}
            inputProps={{ value: attachments }}
            readOnly
            size='small'
            mt={1}
          />
        )}
      </InspectionTaskCardActionWrapper>
    </Card>
  )
}
